'use client';

import { type JSX } from 'react';

import { cn, Text } from '@videoblocks/design-system';
import { ControlsPause, ControlsPlay } from '@videoblocks/design-system/icons';

import { formatTimeDisplay } from '../../FilmStripSlider/videoUtils';

const ICON_SIZE = 13;

export type VideoEditPlayerControlsProps = {
    isPlaying: boolean;
    /** Playhead position in the source video, so the readout tracks real footage time. */
    currentTime: number;
    /** Full duration of the source video, not of the selected clip. */
    videoDuration: number;
    onPlayPause: () => void;
};

export function VideoEditPlayerControls({
    isPlaying,
    currentTime,
    videoDuration,
    onPlayPause,
}: VideoEditPlayerControlsProps): JSX.Element {
    return (
        <div className="absolute bottom-0 left-0 right-0">
            <div
                aria-hidden="true"
                className="absolute bottom-0 left-0 right-0 h-24 bg-linear-to-t from-black to-transparent pointer-events-none"
            />
            <div className="relative flex items-center gap-2.5 px-4 pb-2.5">
                <button
                    type="button"
                    onClick={onPlayPause}
                    className="flex items-center justify-center rounded-xs p-1 transition-colors text-neutral-dark-1000 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                >
                    <PlaybackIcon isPlaying={isPlaying} className="text-neutral-dark-1000" />
                </button>
                <Text fontSize="xs" className="text-neutral-dark-1000" data-testid="video-time-display">
                    {formatTimeDisplay(currentTime)} / {formatTimeDisplay(videoDuration)}
                </Text>
            </div>
        </div>
    );
}

/* ——— Subcomponents —————————————————————————————————————————————————————————— */

function PlaybackIcon({ isPlaying, className }: { isPlaying: boolean; className?: string }): JSX.Element {
    // The glyphs carry path-level fills, so an override has to reach the paths themselves.
    const iconClassName = cn('[&_path]:fill-white', className);

    if (isPlaying) {
        return <ControlsPause width={ICON_SIZE} height={ICON_SIZE} className={iconClassName} />;
    }
    return <ControlsPlay width={ICON_SIZE} height={ICON_SIZE} className={iconClassName} />;
}
